import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";
import { formatDateDb, formatDateHourDb } from "../utils/tools";
import { customDateFormatType } from "../utils/utils";

const NUMBER_NFT_PER_PAGE = NumberOfPerPage.actionHistoryManagement;
const queryKey = "actionHistory";

async function fetchActionHistory(
  currentPage,
  type,
  action,
  performedBy,
  startTime,
  endTime,
) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    if (type) options.type = type;
    if (action) options.action = action;
    if (performedBy) options.performedBy = performedBy;
    if (startTime) options.startTime = customDateFormatType(startTime);
    if (endTime) options.endTime = customDateFormatType(endTime);

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI("get", `/actionHistory?${queryParams}`);
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries,
    );

    const newData = data?.items.map((item, index) => {
      return {
        id: item?._id || "-",
        codeEmployee: item?.performedByObject?.code || "-",
        nameEmployee: item?.performedByObject?.fullName || "-",
        type: getType(item?.type) || "-",
        action: getAction(item?.action) || "-",
        createdAt: item?.createdAt ? formatDateHourDb(item?.createdAt) : "-",
        edit: {},
        detail: {
          id: item?._id || "-",
          codeEmployee: item?.performedByObject?.code || "-",
          nameEmployee: item?.performedByObject?.fullName || "-",
          type: getType(item?.type) || "-",
          action: getAction(item?.action) || "-",
          oldValue: item?.oldValue,
          newValue: item?.newValue,
          createdAt: item?.createdAt ? formatDateHourDb(item?.createdAt) : "-",
        },
      };
    });

    return { data: newData, totalPages, entries, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching checkIn data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useActionHistory(
  type,
  action,
  performedBy,
  startTime,
  endTime,
) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [
      queryKey,
      currentPage,
      type,
      action,
      performedBy,
      startTime,
      endTime,
    ],
    queryFn: () =>
      fetchActionHistory(
        currentPage,
        type,
        action,
        performedBy,
        startTime,
        endTime,
      ),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    actionHistoryManagementData: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}

const getType = (type) => {
  switch (type) {
    case "BOOKING":
      return "BOOKING";
    case "LOCKING":
      return "LOCKING";
    case "TTKQ":
      return "TTKQ";
    case "HDMB":
      return "HĐMB";
    case "CUSTOMER":
      return "Khách hàng";
    case "CUSTOMER_UPDATE_REQUEST":
      return "Phiếu chỉnh sửa khách hàng";
    case "APARTMENT":
      return "Sản phẩm / căn hộ";
    case "AGENT":
      return "Đại lý";
    case "PROJECT":
      return "Dự án";
    case "EMPLOYEE":
      return "Nhân viên";
    case "CHECKIN":
      return "Chấm công";
    case "OTHER":
      return "Khác";
    default:
      return null; // Trường hợp không khớp
  }
};

const getAction = (action) => {
  switch (action) {
    case "CREATE":
      return "Tạo mới";
    case "UPDATE":
      return "Chỉnh sửa";
    case "DELETE":
      return "Xóa";
    case "APPROVED":
      return "Trạng thái chấp nhận";
    case "REFUND":
      return "Trạng thái hoàn trả";
    case "REJECTED":
      return "Trạng thái từ chối";
    case "EDITING":
      return "Trạng thái yêu cầu chỉnh sửa";
    case "SHARE":
      return "Chia sẻ";
    case "REVERT":
      return "Trở về trạng thái tạo mới";
    case "TRANSFER":
      return "Chuyển đổi";
    case "CANCEL":
      return "Hủy phiếu tạo khách hàng";
    case "PENDING_RETURN_REQUEST":
      return "Chờ duyệt hoàn trả";
    case "REJECT_RETURN_REQUEST":
      return "Từ chối hoàn trả";
    case "CONFIRM_COMMISSION":
      return "Xác nhận phí dịch vụ";
    case "UPDATE_CONFIRM":
      return "Cập nhật xác nhận";
    case "CLOSE":
      return "Đóng dự án";
    case "OPEN":
      return "Mở dự án";
    case "UPDATE_PASSWORD":
      return "Cập nhập mật khẩu";
    case "VERIFY_CHECKIN_PLACE":
      return "Xác nhận địa chỉ chấm công";
    case "VERIFY_BSS_ID":
      return "Xác nhận BSSID";
    case "VERIFY_QR_CODE":
      return "Xác nhận QR Code";
    case "VERIFY_IMAGE":
      return "Xác nhận ảnh chấm công";
    case "COMPLETE_CHECKIN":
      return "Hoàn thành chấm công";
    case "OTHER":
      return "Khác";
    default:
      return null;
  }
};
